import React, { useState } from 'react';
import { CreditCard, Smartphone, X, Check, Plus, Trash2 } from 'lucide-react';

const PaymentMethodsModal = ({ isOpen, onClose, paymentMethods, setPaymentMethods }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMethod, setNewMethod] = useState({ type: 'UPI', details: '' });

  if (!isOpen) return null;

 
  const handleSetDefault = (id) => {
    setPaymentMethods(
      paymentMethods.map(method => ({
        ...method,
        default: method.id === id
      }))
    );
  };

  const handleDeleteMethod = (id) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      setPaymentMethods(paymentMethods.filter(method => method.id !== id));
    }
  };

  const handleAddMethod = () => {
    if (!newMethod.details.trim()) {
      alert('Please enter payment details');
      return;
    }

    const newId = Math.max(...paymentMethods.map(m => m.id), 0) + 1;
    setPaymentMethods([
      ...paymentMethods,
      {
        id: newId,
        type: newMethod.type,
        details: newMethod.details,
        default: paymentMethods.length === 0
      }
    ]);

    setNewMethod({ type: 'UPI', details: '' });
    setShowAddForm(false);
    alert('✅ Payment method added successfully!');
  };

  const getMethodIcon = (type) => {
    switch (type) {
      case 'UPI':
        return <Smartphone size={24} className="text-purple-600" />;
      case 'Card':
        return <CreditCard size={24} className="text-blue-600" />;
      default:
        return <CreditCard size={24} className="text-gray-600" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Payment Methods</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:bg-gray-100 rounded-full p-2 transition-all"
          >
            <X size={24} />
          </button>
        </div>

        {/* Payment Methods List */}
        {paymentMethods.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard size={64} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-6">No payment methods added yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all"
            >
              Add Payment Method
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-6">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`bg-gradient-to-br ${
                    method.type === 'UPI'
                      ? 'from-purple-50 to-purple-100 border-purple-200'
                      : 'from-blue-50 to-blue-100 border-blue-200'
                  } border rounded-xl p-4 ${
                    method.default ? 'ring-2 ring-green-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getMethodIcon(method.type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-800">
                            {method.type}
                          </p>
                          {method.default && (
                            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Check size={12} />
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {method.details}
                        </p>
                        {!method.default && (
                          <button
                            onClick={() => handleSetDefault(method.id)}
                            className="text-xs text-blue-600 hover:underline mt-2"
                          >
                            Set as default
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteMethod(method.id)}
                      className="text-red-500 hover:bg-red-50 rounded-full p-2 transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full border-2 border-dashed border-gray-300 rounded-xl py-4 text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add New Payment Method
              </button>
            )}
          </>
        )}

        {/* Add Payment Method Form */}
        {showAddForm && (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200 mt-4">
            <h3 className="font-semibold text-gray-800 mb-4">
              Add Payment Method
            </h3>

            <div className="space-y-4">
              {/* Payment Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Type
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewMethod({ ...newMethod, type: 'UPI' })}
                    className={`flex-1 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      newMethod.type === 'UPI'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 text-gray-600 hover:border-purple-300'
                    }`}
                  >
                    <Smartphone size={20} />
                    UPI
                  </button>
                  <button
                    onClick={() => setNewMethod({ ...newMethod, type: 'Card' })}
                    className={`flex-1 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                      newMethod.type === 'Card'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:border-blue-300'
                    }`}
                  >
                    <CreditCard size={20} />
                    Card
                  </button>
                </div>
              </div>

              {/* Payment Details Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {newMethod.type === 'UPI' ? 'UPI ID' : 'Card Number'}
                </label>
                <input
                  type="text"
                  value={newMethod.details}
                  onChange={(e) =>
                    setNewMethod({ ...newMethod, details: e.target.value })
                  }
                  placeholder={
                    newMethod.type === 'UPI'
                      ? 'yourname@upi'
                      : '**** **** **** 1234'
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewMethod({ type: 'UPI', details: '' });
                  }}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMethod}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Add Method
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">
            🔒 Secure Payments
          </h4>
          <p className="text-xs text-gray-600">
            Your payment information is encrypted and stored securely. We never
            share your payment details with third parties.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsModal;